import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {PaginationControlsBar} from 'shells/pagination-controls-bar/pagination-controls-bar';

import type {GetProspectorSavedFilters_prospectorSavedFilters as SavedFilter} from 'nutshell-graphql-types';

import {FilterDrawerMainContentWrapper} from '../../../core/entity-list/layout/filter-drawer-main-content-wrapper';
import {PageContainer} from '../../../layout/page-container';

import {getUrlIsDirty} from '../../../../saved-filters/saved-filters-utils';

import {useCreateProspectorContacts} from './graphql/hooks/use-create-prospector-contacts';
import {useGetProspectorContacts} from './graphql/hooks/use-get-prospector-contacts';
import {useGetProspectorSavedFilters} from './graphql/hooks/use-get-prospector-saved-filters';
import {
    savedFilterToOption,
    findMatchingFilter,
    pruneMustHaveFilters,
    buildDefaultFiltersObject,
    calculateTotalPages,
} from './helpers';
import {ProspectorFilterDrawer} from './prospector-filter-drawer';
import {ProspectorMainContent} from './prospector-main-content';
import {ProspectorPageControlBar} from './prospector-page-control-bar';
import {ProspectorPageTitleBar} from './prospector-page-title-bar';

import styles from './prospector-page.module.css';

// limit on backend: Nutshell\GraphQL\Type\Mcfx\Prospector/ProspectorContacts.php
export const DEFAULT_PAGE_SIZE = 50;

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    onRemoveAllFilters: () => void;
    onUpdateFilters: (arg1: FilterObject) => void;
    onParamChange: (arg1: any, arg2: boolean) => void;
};

export function ProspectorListPage(props: Props) {
    const {onUpdateFilters, onParamChange} = props;

    const [selectedFilter, setSelectedFilter] = React.useState<SavedFilter | null | undefined>(
        null
    );
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(true);
    const [isLoadingFromSelectedFilter, setIsLoadingFromSelectedFilter] = React.useState(true);
    const [currentPage, setCurrentPage] = React.useState(1);
    // we keep track of the cursor for each page (so we know where to start fetching from)
    const [cursors, setCursors] = React.useState([0]);

    const propertyBasedFilters = pruneMustHaveFilters(props.activeFilters);

    const {
        prospectorContacts,
        totalCount,
        nextSkip,
        isLoading,
        refetch: refetchProspectorContacts,
        // @ts-expect-error
    } = useGetProspectorContacts(props.activeFilters, cursors[currentPage - 1]);
    const {
        savedFilters,
        refetch,
        isLoading: isLoadingSavedFilters,
    } = useGetProspectorSavedFilters();
    const {createProspectorContacts, isLoading: isCreatingContacts} = useCreateProspectorContacts();

    const numFetchedContacts = prospectorContacts.length;
    const totalPages = calculateTotalPages(totalCount, numFetchedContacts, currentPage);

    const onToggleFilterDrawerShown = () => setIsFilterDrawerOpen(!isFilterDrawerOpen);

    const updateFilters = React.useCallback(
        (newFilters: FilterObject) => {
            onUpdateFilters(newFilters);
        },
        [onUpdateFilters]
    );
    const handlePageChange = (selectedPage: number) => {
        setCurrentPage(selectedPage);
    };
    const handleRefetchProspectorContacts = () => {
        // purge the skips upto the currentPage index
        setCursors(cursors.slice(0, currentPage));
        refetchProspectorContacts(props.activeFilters, cursors[currentPage - 1]);
    };
    // @ts-expect-error
    const handleSetSelectedFilter = (filter) => {
        if (!filter) {
            setSelectedFilter(null);

            return;
        }
        const newSavedFilter = savedFilters.find((f) => f.id === filter.value);
        // do not try updating if the filter is already selected
        if (!newSavedFilter || newSavedFilter.id === (selectedFilter && selectedFilter.id)) {
            return;
        }

        props.onRemoveAllFilters();
        setIsLoadingFromSelectedFilter(true);
        setSelectedFilter(newSavedFilter);
    };
    // when we remove all filters, we still wanna persist the default filters
    const handleRemoveAllFilters = () => {
        const presistedDefaultFilters = buildDefaultFiltersObject(props.activeFilters);
        onParamChange(presistedDefaultFilters, false);
    };

    React.useEffect(() => {
        const urlFilters = Routing.deparam(window.location.search);
        // if the url filters match a saved filter, we select it for the user
        const filter = findMatchingFilter(urlFilters, savedFilters);
        if (filter) {
            setSelectedFilter(filter);
        }
    }, [savedFilters]);

    React.useEffect(() => {
        if (!selectedFilter || !isLoadingFromSelectedFilter) {
            return;
        }

        const newFilters = Routing.deparam(selectedFilter.filterString);
        onParamChange(newFilters, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilter, isLoadingFromSelectedFilter]);

    React.useEffect(() => {
        if (nextSkip && cursors.length <= currentPage) {
            setCursors([...cursors, nextSkip]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextSkip, cursors]);

    // when we change the filters, we wanna purge our cursors and start from the beginning
    React.useEffect(() => {
        setCursors([0]);
        setCurrentPage(1);
    }, [props.activeFilters]);

    return (
        <PageContainer
            noPadding={true}
            title='ProspectorIQ'
            pageTitleBar={
                <ProspectorPageTitleBar
                    hasChanges={getUrlIsDirty(
                        selectedFilter,
                        window.location.search,
                        Boolean(propertyBasedFilters.length)
                    )}
                    refetch={refetch}
                    selectedFilterId={selectedFilter && selectedFilter.id}
                />
            }
            controlBar={
                <ProspectorPageControlBar
                    activeFilters={propertyBasedFilters}
                    onRemoveAllFilters={handleRemoveAllFilters}
                    isFilterDrawerOpen={isFilterDrawerOpen}
                    onToggleFilterDrawerShown={onToggleFilterDrawerShown}
                    selectedFilter={selectedFilter && savedFilterToOption(selectedFilter)}
                    setSelectedFilter={handleSetSelectedFilter}
                    savedFilters={savedFilters.map((filter) => savedFilterToOption(filter))}
                    refetchSavedFilters={refetch}
                    isLoadingSavedFilters={isLoadingSavedFilters}
                />
            }
        >
            <div className='full-height pos-relative'>
                <ProspectorFilterDrawer
                    activeFilters={props.activeFilters}
                    isFilterDrawerOpen={isFilterDrawerOpen}
                    onUpdateFilters={(newFilters) => {
                        if (isLoadingFromSelectedFilter) {
                            setIsLoadingFromSelectedFilter(false);
                        }
                        updateFilters(newFilters);
                    }}
                />
                <FilterDrawerMainContentWrapper
                    isFilterDrawerOpen={isFilterDrawerOpen}
                    hasFlex={true}
                >
                    {propertyBasedFilters.length > 0 && totalPages >= 1 && (
                        <div className={styles['pagination-controls-bar']}>
                            <PaginationControlsBar
                                collectionLength={
                                    numFetchedContacts + (currentPage - 1) * DEFAULT_PAGE_SIZE
                                }
                                currentPage={currentPage}
                                pageSize={DEFAULT_PAGE_SIZE}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                disableButtons={isLoading || isCreatingContacts}
                                disableNextButton={prospectorContacts.length < DEFAULT_PAGE_SIZE}
                                shouldShowTotal={false}
                            />
                        </div>
                    )}
                    <ProspectorMainContent
                        prospectorContacts={prospectorContacts}
                        isLoading={isLoading}
                        numFetchedContacts={numFetchedContacts}
                        onRemoveAllFilters={handleRemoveAllFilters}
                        onRefetch={handleRefetchProspectorContacts}
                        filters={props.activeFilters}
                        createProspectorContacts={createProspectorContacts}
                    />
                </FilterDrawerMainContentWrapper>
            </div>
        </PageContainer>
    );
}
